import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Add,
  Payment,
  Receipt,
  ArrowBack,
  Schedule,
  CheckCircle,
  Pending,
  Error as ErrorIcon,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ReceiptLong,
  Visibility,
  TrendingFlat,
  AccountBalanceWallet,
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

export const InvSupplierPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [financialSummary, setFinancialSummary] = useState(null);

  const [paymentData, setPaymentData] = useState({
    amount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMethod: "bank_transfer",
    paymentType: "grn_payment",
    reference: "",
    notes: "",
    status: "completed",
    grnId: "",
  });

  useEffect(() => {
    fetchPayments();
    fetchFinancialSummary();
  }, [id]);

  const fetchPayments = async () => {
    try {
      const response = await suppliersAPI.getPayments(id);
      setPayments(response.data.data.payments || []);
    } catch (error) {
      enqueueSnackbar("Error fetching payments", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await suppliersAPI.getFinancialSummary(id);
      setFinancialSummary(response.data.data);
    } catch (error) {
      console.error("Error fetching financial summary:", error);
    }
  };

  const handlePaymentAdded = () => {
    enqueueSnackbar("Payment recorded successfully", { variant: "success" });
    fetchPayments();
    fetchFinancialSummary();
    setPaymentDialog(false);
    setPaymentData({
      amount: "",
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMethod: "bank_transfer",
      paymentType: "grn_payment",
      reference: "",
      notes: "",
      status: "completed",
      grnId: "",
    });
  };

  const handleAddPayment = async () => {
    try {
      await suppliersAPI.addPayment(id, paymentData);
      handlePaymentAdded();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error recording payment",
        { variant: "error" }
      );
    }
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setViewDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "success",
      pending: "warning",
      failed: "error",
      draft: "default",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircle fontSize="small" />,
      pending: <Pending fontSize="small" />,
      failed: <ErrorIcon fontSize="small" />,
      draft: <Schedule fontSize="small" />,
      cancelled: <ErrorIcon fontSize="small" />,
    };
    return icons[status] || <Pending fontSize="small" />;
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      bank_transfer: "primary",
      cheque: "secondary",
      cash: "success",
      upi: "info",
      card: "warning",
      online: "info",
      credit_note: "success",
    };
    return colors[method] || "default";
  };

  const getPaymentTypeColor = (type) => {
    const colors = {
      grn_payment: "primary",
      advance_payment: "info",
      return_settlement: "success",
      credit_note: "success",
      debit_note: "error",
      account_settlement: "secondary",
    };
    return colors[type] || "default";
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Box sx={{ p: 4 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
          p: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/hospital/hospital-inventory/suppliers")}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Back to Suppliers
          </Button>
          <Typography 
            variant="h3" 
            fontWeight="bold"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Payments & Settlements
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setPaymentDialog(true)}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            },
          }}
        >
          Record Payment
        </Button>
      </Box>

      {/* Enhanced Financial Summary */}
      {financialSummary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              icon: <ReceiptLong sx={{ fontSize: 48 }} />,
              value: financialSummary.financialSummary?.totalGRNValue || 0,
              label: "Total GRN Value",
              color: "primary.main",
              bgColor: `${theme.palette.primary.main}15`,
            },
            {
              icon: <Payment sx={{ fontSize: 48 }} />,
              value: financialSummary.financialSummary?.totalPayments || 0,
              label: "Total Paid",
              color: "success.main",
              bgColor: `${theme.palette.success.main}15`,
            },
            {
              icon: <AccountBalanceWallet sx={{ fontSize: 48 }} />,
              value: financialSummary.financialSummary?.outstandingBalance || 0,
              label: "Outstanding",
              color: "warning.main",
              bgColor: `${theme.palette.warning.main}15`,
            },
            {
              icon: <TrendingDown sx={{ fontSize: 48 }} />,
              value: financialSummary.financialSummary?.availableCredits || 0,
              label: "Available Credits",
              color: "info.main",
              bgColor: `${theme.palette.info.main}15`,
            },
            {
              icon: <AccountBalance sx={{ fontSize: 48 }} />,
              value: financialSummary.financialSummary?.netPayable || 0,
              label: "Net Payable",
              color: "secondary.main",
              bgColor: `${theme.palette.secondary.main}15`,
            },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card 
                sx={{ 
                  borderRadius: 3,
                  background: item.bgColor,
                  border: `1px solid ${alpha(theme.palette[item.color.split('.')[0]].main, 0.2)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Box sx={{ color: item.color, mb: 2 }}>
                    {item.icon}
                  </Box>
                  <Typography 
                    variant="h3" 
                    fontWeight="bold" 
                    color={item.color}
                    sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
                  >
                    ₹{item.value?.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ mt: 1, fontWeight: 500 }}
                  >
                    {item.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Payment Tabs */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 600,
                py: 2,
                px: 3,
                minHeight: '60px',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
              '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: 2,
              },
            }}
          >
            <Tab label="All Payments" />
            <Tab label="GRN Payments" />
            <Tab label="Advance Payments" />
            <Tab label="Settlements" />
            <Tab label="Pending" />
          </Tabs>
        </CardContent>
      </Card>

      {/* Filtered Payments */}
      <TabPanel value={tabValue} index={0}>
        <PaymentTable
          payments={payments}
          onView={handleViewPayment}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getPaymentMethodColor={getPaymentMethodColor}
          getPaymentTypeColor={getPaymentTypeColor}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <PaymentTable
          payments={payments.filter((p) => p.paymentType === "grn_payment")}
          onView={handleViewPayment}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getPaymentMethodColor={getPaymentMethodColor}
          getPaymentTypeColor={getPaymentTypeColor}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <PaymentTable
          payments={payments.filter((p) => p.paymentType === "advance_payment")}
          onView={handleViewPayment}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getPaymentMethodColor={getPaymentMethodColor}
          getPaymentTypeColor={getPaymentTypeColor}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <PaymentTable
          payments={payments.filter((p) =>
            ["return_settlement", "credit_note", "account_settlement"].includes(
              p.paymentType
            )
          )}
          onView={handleViewPayment}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getPaymentMethodColor={getPaymentMethodColor}
          getPaymentTypeColor={getPaymentTypeColor}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <PaymentTable
          payments={payments.filter((p) => p.status === "pending")}
          onView={handleViewPayment}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getPaymentMethodColor={getPaymentMethodColor}
          getPaymentTypeColor={getPaymentTypeColor}
        />
      </TabPanel>

      {/* Add Payment Dialog */}
      <Dialog 
        open={paymentDialog} 
        onClose={() => setPaymentDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 700,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          pb: 1,
        }}>
          Record New Payment
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Type"
                value={paymentData.paymentType}
                onChange={(e) => setPaymentData(prev => ({ ...prev, paymentType: e.target.value }))}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '1.1rem', borderRadius: 2 },
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                }}
              >
                <MenuItem value="grn_payment" sx={{ fontSize: '1.1rem' }}>GRN Payment</MenuItem>
                <MenuItem value="advance_payment" sx={{ fontSize: '1.1rem' }}>Advance Payment</MenuItem>
                <MenuItem value="return_settlement" sx={{ fontSize: '1.1rem' }}>Return Settlement</MenuItem>
                <MenuItem value="account_settlement" sx={{ fontSize: '1.1rem' }}>Account Settlement</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={paymentData.amount}
                onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ fontSize: '1.1rem' }}>₹</InputAdornment>,
                }}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '1.1rem', borderRadius: 2 },
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Payment Date"
                value={paymentData.paymentDate}
                onChange={(e) => setPaymentData(prev => ({ ...prev, paymentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '1.1rem', borderRadius: 2 },
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '1.1rem', borderRadius: 2 },
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                }}
                required
              >
                <MenuItem value="bank_transfer" sx={{ fontSize: '1.1rem' }}>Bank Transfer</MenuItem>
                <MenuItem value="cheque" sx={{ fontSize: '1.1rem' }}>Cheque</MenuItem>
                <MenuItem value="cash" sx={{ fontSize: '1.1rem' }}>Cash</MenuItem>
                <MenuItem value="upi" sx={{ fontSize: '1.1rem' }}>UPI</MenuItem>
                <MenuItem value="card" sx={{ fontSize: '1.1rem' }}>Card</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference"
                value={paymentData.reference}
                onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Transaction ID, Cheque No."
                sx={{
                  '& .MuiInputBase-root': { fontSize: '1.1rem', borderRadius: 2 },
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '1.1rem', borderRadius: 2 },
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setPaymentDialog(false)}
            sx={{ 
              fontSize: '1.1rem', 
              px: 4, 
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddPayment}
            variant="contained"
            disabled={!paymentData.amount || paymentData.amount <= 0}
            sx={{ 
              fontSize: '1.1rem', 
              px: 4, 
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Payment Details Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 700,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          pb: 1,
        }}>
          Payment Details
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Payment Number
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {selectedPayment.paymentNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Amount
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  ₹{selectedPayment.amount?.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Payment Type
                </Typography>
                <Chip
                  label={selectedPayment.paymentType?.replace(/_/g, " ") || "Payment"}
                  color={getPaymentTypeColor(selectedPayment.paymentType)}
                  size="medium"
                  sx={{ fontSize: '1rem', px: 1, py: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  icon={getStatusIcon(selectedPayment.status)}
                  label={selectedPayment.status}
                  color={getStatusColor(selectedPayment.status)}
                  size="medium"
                  sx={{ fontSize: '1rem', px: 1, py: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Payment Method
                </Typography>
                <Chip
                  label={selectedPayment.paymentMethod?.replace(/_/g, " ") || "N/A"}
                  color={getPaymentMethodColor(selectedPayment.paymentMethod)}
                  size="medium"
                  sx={{ fontSize: '1rem', px: 1, py: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Date
                </Typography>
                <Typography variant="h6" fontWeight="medium">
                  {new Date(selectedPayment.paymentDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Grid>
              {selectedPayment.reference && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Reference
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {selectedPayment.reference}
                  </Typography>
                </Grid>
              )}
              {selectedPayment.notes && (
                <Grid item xs={12}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    {selectedPayment.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setViewDialog(false)}
            variant="contained"
            sx={{ 
              fontSize: '1.1rem', 
              px: 4, 
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};

// Enhanced Payment Table Component
const PaymentTable = ({
  payments,
  onView,
  getStatusColor,
  getStatusIcon,
  getPaymentMethodColor,
  getPaymentTypeColor,
}) => {
  const theme = useTheme();

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Card 
      sx={{ 
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Payment History ({payments.length})
          </Typography>
        </Box>

        {payments.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Alert 
              severity="info" 
              sx={{ 
                fontSize: '1.1rem',
                borderRadius: 2,
                '& .MuiAlert-message': { fontSize: '1.1rem' }
              }}
            >
              No payment records found.
            </Alert>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
                }}>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Date</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Type</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Reference</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Method</TableCell>
                  <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Status</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow 
                    key={payment._id} 
                    hover
                    sx={{ 
                      '&:last-child td': { borderBottom: 0 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: `${theme.palette.primary.main}08`,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: '1.1rem', py: 2.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={payment.paymentType?.replace(/_/g, " ") || "Payment"}
                        size="medium"
                        color={getPaymentTypeColor(payment.paymentType)}
                        variant="outlined"
                        sx={{ fontSize: '1rem', fontWeight: 600, px: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {payment.reference || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={payment.paymentMethod?.replace(/_/g, " ") || "N/A"}
                        size="medium"
                        color={getPaymentMethodColor(payment.paymentMethod)}
                        variant="outlined"
                        sx={{ fontSize: '1rem', fontWeight: 600, px: 1 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2.5 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          color: ["return_settlement", "credit_note"].includes(
                            payment.paymentType
                          )
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                          fontSize: '1.2rem',
                        }}
                      >
                        {["return_settlement", "credit_note"].includes(
                          payment.paymentType
                        )
                          ? "+"
                          : ""}
                        ₹{payment.amount?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        icon={getStatusIcon(payment.status)}
                        label={payment.status}
                        size="medium"
                        color={getStatusColor(payment.status)}
                        sx={{ fontSize: '1rem', fontWeight: 600, px: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="large"
                          onClick={() => onView(payment)}
                          color="primary"
                          sx={{
                            background: `${theme.palette.primary.main}15`,
                            '&:hover': {
                              background: `${theme.palette.primary.main}30`,
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Visibility fontSize="large" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
    </Box>
  );
};